import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDaysAndHoursAndMinutes(
  value: number,
  unit: "milliseconds" | "seconds" | "minutes" | "hours" | "days",
  stopAt?: "days" | "hours" | "minutes" | "seconds",
): { days: number; hours: number; minutes: number; seconds: number } {
  const multipliers = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
  };

  const totalMilliseconds = value * multipliers[unit];

  // If stopAt is not provided, use the original behavior
  if (!stopAt) {
    const days = Math.floor(totalMilliseconds / (24 * 60 * 60 * 1000));
    const hours = Math.floor(
      (totalMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
    );
    const minutes = Math.floor(
      (totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000),
    );
    const seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000);

    return { days, hours, minutes, seconds };
  }

  // When stopAt is provided, stop extraction at that unit and add higher units to it
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (stopAt === "days") {
    days = Math.floor(totalMilliseconds / (24 * 60 * 60 * 1000));
    hours = Math.floor(
      (totalMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000),
    );
    minutes = Math.floor((totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000));
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000);
  } else if (stopAt === "hours") {
    // Convert days to hours, don't extract days
    const totalHours = Math.floor(totalMilliseconds / (60 * 60 * 1000));
    hours = totalHours;
    minutes = Math.floor((totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000));
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000);
  } else if (stopAt === "minutes") {
    // Convert days and hours to minutes, don't extract days or hours
    const totalMinutes = Math.floor(totalMilliseconds / (60 * 1000));
    minutes = totalMinutes;
    seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000);
  } else if (stopAt === "seconds") {
    // Convert everything to seconds, don't extract days, hours, or minutes
    seconds = Math.floor(totalMilliseconds / 1000);
  }

  return { days, hours, minutes, seconds };
}

// DD/MM/YYYY string → Date object
export const ddmmyyyyToDate = (value: string): Date | undefined => {
  if (!value) return undefined;
  const parts = value.split("/");
  if (parts.length !== 3) return undefined;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y) return undefined;
  return new Date(y, m - 1, d);
};

// Date object → DD/MM/YYYY string
export const dateToDDMMYYYY = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};
